export const UserRole = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  STANDARD: 'standard',
  ENGINEER: 'engineer',
  DEVELOPER: 'developer',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export interface User {
  last_name: string;
  first_name: string;
  id: number;
  email: string;
  name: string;
  phone?: string;
  ws_id?: string | null;
  company_id?: string;
  role: UserRole;
  profile_picture?: {
    access?: string;
    path?: string;
    name?: string;
    type?: string;
    size?: number;
    mime?: string;
    meta?: {
      width: number;
      height: number;
    };
    url: string;
  } | null;
  google_oauth?: {
    id: string;
    name: string;
    email: string;
  };
  _company?: {
    id: string;
    name: string;
  };
}

export type ActionType = 'view' | 'create' | 'edit' | 'delete';
export type ResourceType =
  | 'invoices'
  | 'company'
  | 'team_users'
  | 'integrations'
  | 'worksheet_pass'
  | 'prompts';

export type ResourceAction = `${ActionType}_${ResourceType}`;

export const AccessPermission: Record<UserRole, ResourceAction[]> = {
  [UserRole.ADMIN]: [
    // Invoices
    'view_invoices',
    'edit_invoices',
    'delete_invoices',
    // Company
    'edit_company',
    // Team
    'view_team_users',
    'create_team_users',
    'edit_team_users',
    'delete_team_users',
    // Integrations (company-owned)
    'edit_integrations',
    // Worksheet Pass: Xano refuses anyone whose role is not admin
    'edit_worksheet_pass',
    // Team Prompt Templates and groups: Xano refuses other roles
    'edit_prompts',
  ],
  [UserRole.MANAGER]: [
    // Invoices
    'view_invoices',
    'edit_invoices',
    'delete_invoices',
    // Company
    'edit_company',
    // Team
    'view_team_users',
    // Team Prompt Templates and groups: Xano refuses other roles
    'edit_prompts',
  ],
  [UserRole.STANDARD]: [],
  [UserRole.ENGINEER]: [],
  [UserRole.DEVELOPER]: [],
};

export type UpdateProfileData = {
  firstName: string;
  lastName: string;
  phone: string;
};

export type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

export type JoinCompanyData = {
  token: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};

export type CreateCompanyRegistrationData = {
  companyName: string;
  companyAddress1: string;
  companyAddress2: string;
  companyCity: string;
  companyPostcode: string;
  companyWebsite: string;
  companyRole: string;
};

export type SetupCompanyBcIntegrationRequest = {
  clientId: string;
  clientSecret: string;
  customerId: string;
};

export type CompanyRegistration = {
  address_1: string;
  address_2: string;
  backoffice_count: number;
  bc_client_id: string;
  bc_customer_id: number;
  bc_secret: string;
  company_id: null;
  company_name: string;
  company_website: string;
  created_at: number;
  id: string;
  industry: string;
  job_volume_per_month: number;
  postcode: string;
  resource_count: number;
  status: string;
  town: string;
  updated_at: number;
  user_email: string;
  user_first: string;
  user_id: number;
  user_last: string;
  user_mobile: number;
  vh3_api_key: string;
};

export type RegisterData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};
